import {
  type ComponentProps,
  useCallback,
  useMemo,
  useRef,
  useState,
} from "react";
import { DocumentView } from "../pathologist-review/DocumentView";
import {
  buildReportCompositionParts,
  compositionReportData,
  type ReportComposition,
} from "../../utils/reportComposition";
import { ReportCompositionFile } from "./ReportCompositionFile";
import { ReportCompositionSection } from "./ReportCompositionSection";
import type {
  ReportFooterProps,
  ReportHeaderSectionProps,
} from "./ReportAttachmentPage";

interface Props {
  caseId: string;
  composition: ReportComposition;
  documentProps: ComponentProps<typeof DocumentView>;
  internal: boolean;
  footer: Omit<ReportFooterProps, "pageNumber" | "totalPages">;
  headerSectionProps?: ReportHeaderSectionProps;
  onSettled: (count: number) => void;
  onPending: () => void;
  onError: () => void;
}

export function ReportCompositionDocument(props: Props) {
  const {
    composition,
    documentProps,
    onSettled,
    onPending,
    caseId,
    internal,
    footer,
    headerSectionProps,
    onError,
  } = props;
  const parts = useMemo(
    () =>
      buildReportCompositionParts(
        compositionReportData(documentProps.documentViewDataOverride!),
        composition
      ),
    [composition, documentProps.documentViewDataOverride]
  );
  const countRef = useRef<Record<string, number>>({});
  const [counts, setCounts] = useState<Record<string, number>>({});
  const total = useMemo(
    () => parts.reduce((sum, part) => sum + (counts[part.id] || 1), 0),
    [counts, parts]
  );
  const handleSettled = useCallback(
    (id: string, count: number) => {
      const next = { ...countRef.current, [id]: count };
      countRef.current = next;
      setCounts(next);
      if (parts.every((part) => next[part.id] !== undefined)) {
        onSettled(parts.reduce((sum, part) => sum + next[part.id], 0));
      }
    },
    [onSettled, parts]
  );
  const handlePending = useCallback(
    (id: string) => {
      const next = { ...countRef.current };
      delete next[id];
      countRef.current = next;
      onPending();
    },
    [onPending]
  );

  return (
    <>
      {parts.map((part, index) => {
        const offset = parts
          .slice(0, index)
          .reduce((sum, previous) => sum + (counts[previous.id] || 1), 0);
        return part.kind === "file" ? (
          <ReportCompositionFile
            key={part.id}
            caseId={caseId}
            file={part.file}
            internal={internal}
            footer={footer}
            headerSectionProps={headerSectionProps}
            firstPageNumber={offset + 1}
            totalPages={total}
            onSettled={handleSettled}
            onPending={handlePending}
            onError={onError}
          />
        ) : (
          <ReportCompositionSection
            key={part.id}
            id={part.id}
            onSettled={handleSettled}
            onPending={handlePending}
            documentProps={{
              ...documentProps,
              includeLegacyBaseline: part.main,
              documentViewDataOverride: part.reportData,
              reportPhotos: part.main ? documentProps.reportPhotos : [],
              pageNumberOffset: offset,
              additionalPageCount: total - offset - (counts[part.id] || 1),
            }}
          />
        );
      })}
    </>
  );
}
